import { useState, useEffect, useCallback } from 'react'
import { generateId } from '../utils/gridHelpers.js'

const API_BASE = '/api'

/**
 * Hook for managing shopping lists for a specific store.
 */
export function useShoppingList(storeId) {
  const [lists, setLists] = useState([])
  const [activeList, setActiveList] = useState(null)
  const [loading, setLoading] = useState(true)

  // Fetch all lists for the store
  const fetchLists = useCallback(async () => {
    if (!storeId) return
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE}/stores/${storeId}/lists`)
      if (res.ok) {
        const data = await res.json()
        setLists(data.lists || [])
        // Auto-select first list if none active
        if (data.lists?.length > 0 && !activeList) {
          const list = data.lists[0]
          setActiveList({
            ...list,
            items: JSON.parse(list.items || '[]'),
          })
        }
      }
    } catch (err) {
      console.error('Failed to fetch lists:', err)
    } finally {
      setLoading(false)
    }
  }, [storeId])

  useEffect(() => {
    fetchLists()
  }, [fetchLists])

  // Create a new shopping list
  const createList = useCallback(async (name = 'Boodschappenlijst') => {
    if (!storeId) return null
    try {
      const res = await fetch(`${API_BASE}/stores/${storeId}/lists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      })
      if (res.ok) {
        const data = await res.json()
        await fetchLists()
        setActiveList({ ...data.list, items: [] })
        return data.list
      }
    } catch (err) {
      console.error('Failed to create list:', err)
    }
    return null
  }, [storeId, fetchLists])

  // Select a list
  const selectList = useCallback((list) => {
    setActiveList({
      ...list,
      items: typeof list.items === 'string' ? JSON.parse(list.items) : list.items,
    })
  }, [])

  // Save the current active list to the server
  const saveList = useCallback(async (updatedItems) => {
    if (!activeList?.id || !storeId) return
    try {
      await fetch(`${API_BASE}/stores/${storeId}/lists`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: activeList.id,
          name: activeList.name,
          items: JSON.stringify(updatedItems),
        }),
      })
    } catch (err) {
      console.error('Failed to save list:', err)
    }
  }, [activeList, storeId])

  // Add an item to the active list
  const addItem = useCallback((name, category = 'overig') => {
    if (!activeList) return
    const newItem = {
      id: generateId(),
      name: name.trim(),
      category,
      checked: false,
    }
    const updatedItems = [...activeList.items, newItem]
    setActiveList(prev => ({ ...prev, items: updatedItems }))
    saveList(updatedItems)
  }, [activeList, saveList])

  // Remove an item from the active list
  const removeItem = useCallback((itemId) => {
    if (!activeList) return
    const updatedItems = activeList.items.filter(item => item.id !== itemId)
    setActiveList(prev => ({ ...prev, items: updatedItems }))
    saveList(updatedItems)
  }, [activeList, saveList])

  // Toggle item checked state
  const toggleItem = useCallback((itemId) => {
    if (!activeList) return
    const updatedItems = activeList.items.map(item =>
      item.id === itemId ? { ...item, checked: !item.checked } : item
    )
    setActiveList(prev => ({ ...prev, items: updatedItems }))
    saveList(updatedItems)
  }, [activeList, saveList])

  // Update item category
  const updateItemCategory = useCallback((itemId, category) => {
    if (!activeList) return
    const updatedItems = activeList.items.map(item =>
      item.id === itemId ? { ...item, category } : item
    )
    setActiveList(prev => ({ ...prev, items: updatedItems }))
    saveList(updatedItems)
  }, [activeList, saveList])

  // Delete a list
  const deleteList = useCallback(async (listId) => {
    if (!storeId) return
    try {
      await fetch(`${API_BASE}/stores/${storeId}/lists`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: listId }),
      })
      if (activeList?.id === listId) setActiveList(null)
      await fetchLists()
    } catch (err) {
      console.error('Failed to delete list:', err)
    }
  }, [storeId, activeList, fetchLists])

  return {
    lists,
    activeList,
    loading,
    createList,
    selectList,
    addItem,
    removeItem,
    toggleItem,
    updateItemCategory,
    deleteList,
  }
}
